// Billing section of the one-scroll home (spec §8, revised 2026-07-22: an
// anchored section rather than a separate /billing route — see the home
// one-scroll body in AgentsPage.tsx). Same content as the old billing route:
// total credits, card on file, active subscriptions (Task 8), purchase &
// credit history (Task 9). Money-moving CTAs deep-link to the marketplace
// (§6); only cancel runs in-vault. Flag off -> render nothing (no redirect
// UI needed once the route is gone).

import { useBilling } from '@/src/hooks/useBilling';
import { useConfig } from '@/src/config/context';
import { marketplaceAgentStoreUrl } from '@/lib/marketplace/links';
import { formatCredits, NANOCREDITS_PER_CREDIT } from '@/src/lib/credits';
import { BillingSubscriptions } from '@/src/components/billing/BillingSubscriptions';
import { BillingHistory } from '@/src/components/billing/BillingHistory';

export function BillingSection() {
  const billing = useBilling();
  const config = useConfig();

  if (billing.status === 'disabled') return null;

  return (
    <section className="vault-block vault-manage-block vault-billing" data-anchor="section-billing" aria-label="Billing">
      {billing.status === 'loading' ? (
        <p>Loading billing…</p>
      ) : billing.status !== 'ready' ? (
        <>
          <h2>Billing</h2>
          <p>Billing is unavailable right now.</p>
          <button type="button" onClick={billing.refetch}>Retry</button>
        </>
      ) : (
        <>
          <header className="vault-billing__header">
            <h2>Billing</h2>
            <button type="button" onClick={billing.refetch} aria-label="Refresh billing">Refresh</button>
          </header>

          <section aria-labelledby="billing-total">
            <h2 id="billing-total">Total credits</h2>
            {/* useBilling already divides nc -> credits; recover nc (exact for
                these magnitudes) since the shared formatter takes nanocredits. */}
            <p className="vault-billing__total">{formatCredits((billing.totalCredits ?? 0) * NANOCREDITS_PER_CREDIT)}</p>
          </section>

          <section aria-labelledby="billing-card">
            <h2 id="billing-card">Card on file</h2>
            {billing.card ? (
              <p>
                {billing.card.brand} ···· {billing.card.last4}
                {billing.card.expMonth && billing.card.expYear ? ` · ${billing.card.expMonth}/${billing.card.expYear}` : ''}
              </p>
            ) : (
              <p>No card on file.</p>
            )}
            {billing.subscriptions.length > 0 && (
              // Card is account-wide, not per-agent — any subscribed agent's
              // marketplace modal carries the change-card flow, so the first
              // subscription is as good a deep-link target as any.
              <a
                href={marketplaceAgentStoreUrl(config.marketplaceBaseUrl, billing.subscriptions[0]!.agentId)}
                target="_blank"
                rel="noreferrer"
              >
                Change card
              </a>
            )}
          </section>

          <BillingSubscriptions />
          <BillingHistory />
        </>
      )}
    </section>
  );
}
